import { AnimatePresence, motion } from 'framer-motion'
import { X } from 'lucide-react'
import { type ReactNode, useEffect } from 'react'
import { createPortal } from 'react-dom'

interface DrawerProps {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  side?: 'right' | 'left' | 'bottom'
  widthClassName?: string
}

export function Drawer({ open, onClose, title, children, side = 'right', widthClassName = 'max-w-md' }: DrawerProps) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  const isBottom = side === 'bottom'
  const initial = isBottom ? { y: '100%' } : side === 'right' ? { x: '100%' } : { x: '-100%' }
  const animatePos = isBottom ? { y: 0 } : { x: 0 }

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[200] flex">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="absolute inset-0 bg-ink-950/45 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            initial={initial}
            animate={animatePos}
            exit={{ ...initial, transition: { duration: 0.2 } }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className={
              isBottom
                ? 'relative z-10 mt-auto w-full max-h-[88vh] rounded-t-card border-t border-shell-border bg-shell shadow-pop flex flex-col'
                : `relative z-10 ${side === 'left' ? 'mr-auto' : 'ml-auto'} h-full w-full ${widthClassName} bg-shell shadow-pop flex flex-col`
            }
          >
            {title && (
              <div className="flex items-center justify-between gap-4 border-b border-shell-border px-6 py-4 shrink-0">
                <h2 className="font-display text-lg font-semibold text-shell-text-active">{title}</h2>
                <button
                  aria-label="Close panel"
                  onClick={onClose}
                  className="rounded-lg p-1.5 text-shell-text transition-colors hover:bg-shell-border/50 hover:text-shell-text-active"
                >
                  <X size={18} />
                </button>
              </div>
            )}
            <div className="flex-1 overflow-y-auto scrollbar-thin">{children}</div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
